/* Écritures du fil.
 *
 * Une règle, et c'est la base qui la tient : `auteur` doit être le profil
 * connecté — la politique d'insertion l'exige. On le passe donc toujours, et
 * on ne laisse jamais l'appelant choisir qui a écrit.
 *
 * Les changements ne passent pas par ici seuls : ils sont inscrits par la
 * fonction qui a fait l'écriture, juste après, et jamais à la main. */

import { supabase } from "@/lib/supabase";
import { changements } from "@/domain/journal";
import type { EntreeJournal, SujetJournal } from "@/types/metier";

function echoue(erreur: { message: string } | null): void {
  if (erreur) throw new Error(erreur.message);
}

export interface Porteur {
  sujet: SujetJournal;
  sujetId: string;
  /** Le profil connecté. Sans lui, la base refuse l'écriture. */
  auteur: string;
}

/** Une note : quelqu'un dit quelque chose sur la fiche. */
export async function ajouterNote(p: Porteur, texte: string): Promise<void> {
  const t = texte.trim();
  if (!t) return;
  const { error } = await supabase.from("journal").insert({
    sujet: p.sujet, sujet_id: p.sujetId, nature: "note", texte: t, auteur: p.auteur,
  });
  echoue(error);
}

/** Quelque chose à faire, avec une échéance et quelqu'un dessus. */
export async function planifierActivite(
  p: Porteur,
  a: { texte: string; echeance: string | null; assigne_a: string | null },
): Promise<void> {
  const t = a.texte.trim();
  if (!t) return;
  const { error } = await supabase.from("journal").insert({
    sujet: p.sujet, sujet_id: p.sujetId, nature: "activite",
    texte: t, echeance: a.echeance || null, assigne_a: a.assigne_a || null,
    etat: "a_faire", auteur: p.auteur,
  });
  echoue(error);
}

/** C'est fait, ou ça ne se fera pas. */
export async function cloreActivite(id: string, fait: boolean): Promise<void> {
  const { error } = await supabase
    .from("journal")
    .update({ etat: fait ? "faite" : "annulee", fait_le: new Date().toISOString() })
    .eq("id", id);
  echoue(error);
}

/** Rouvrir ce qu'on a clos trop vite. */
export async function rouvrirActivite(id: string): Promise<void> {
  const { error } = await supabase
    .from("journal").update({ etat: "a_faire", fait_le: null }).eq("id", id);
  echoue(error);
}

export async function corrigerNote(id: string, texte: string): Promise<void> {
  const t = texte.trim();
  if (!t) return;
  const { error } = await supabase.from("journal").update({ texte: t }).eq("id", id);
  echoue(error);
}

export async function retirerNote(id: string): Promise<void> {
  const { error } = await supabase.from("journal").delete().eq("id", id);
  echoue(error);
}

/**
 * La trace d'une modification. On compare l'avant et l'après, et on n'inscrit
 * que ce qui a bougé parmi les champs suivis — le reste n'intéresse personne.
 *
 * Ne lève pas : une trace manquante ne doit jamais faire échouer
 * l'enregistrement qu'elle relate. L'inverse serait absurde.
 */
export async function inscrireChangements(
  p: Porteur,
  avant: Record<string, unknown>,
  apres: Record<string, unknown>,
): Promise<void> {
  const liste = changements(p.sujet, avant, apres);
  if (!liste.length) return;
  await supabase.from("journal").insert(
    liste.map((c) => ({
      sujet: p.sujet, sujet_id: p.sujetId, nature: "changement" as const,
      champ: c.champ, avant: c.avant, apres: c.apres, auteur: p.auteur,
    })),
  );
}

/**
 * Enregistre une fiche et inscrit ce qui a changé, d'un seul geste.
 * Rend la ligne telle que la base l'a gardée.
 */
export async function enregistrerFiche<T extends { id: string }>(
  p: Porteur,
  table: string,
  avant: Record<string, unknown>,
  champs: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase
    .from(table).update(champs).eq("id", p.sujetId).select("*").single();
  echoue(error);
  await inscrireChangements(p, avant, champs);
  return data as T;
}

/** Les notes et activités d'une fiche, pour un export ou un PDF. */
export function lisible(e: EntreeJournal): string {
  if (e.nature === "changement") return `${e.champ} : ${e.avant ?? "—"} → ${e.apres ?? "—"}`;
  return e.texte ?? "";
}
